import {
  AgendaClassificacao,
  AgendaEncaixeRequest,
  AgendaEspecialidadeDaAgenda,
  AgendaNovoAgendamentoRequest,
  AgendaOpcao,
  AgendaPlanoPaciente,
  AgendaPrecoPaciente,
  AgendaRetornoDisponivel,
} from '../../../../interfaces/agenda';

export const CONVENIO_CARTAO = 224;
export const CONVENIO_PARTICULAR = 86;
export const CATEGORIA_PARTICULAR = '13';
export const HORA_ENCAIXE_PADRAO = '08:00';
export const DURACAO_ENCAIXE_PADRAO = 15;
const LIMITE_RETORNO_INFINITO = 999;

export type AgendaModoAgendamento = 'novo' | 'encaixe';
export type AgendaTipoAgendamento = 'CONSULTA' | 'EXAME';

export interface OpcaoClassif {
  /** A sigla que a gravação aceita. */
  valor: string;
  rotulo: string;
  desabilitada: boolean;
}

export const CLASSIF_RETORNO = ['R', 'R13'];
const CLASSIF_EXAME_ENCAIXE = '1';

export function ehRetorno(classif: string | null | undefined): boolean {
  return CLASSIF_RETORNO.includes(classif ?? '');
}

export function classifPadrao(tipo: AgendaTipoAgendamento): string {
  return tipo === 'EXAME' ? '6' : 'P';
}

export function podeEncaixar(ctx: { supervisor: boolean; encaixeLivre: boolean } | null | undefined): boolean {
  return !!ctx && (ctx.supervisor || ctx.encaixeLivre);
}

/**
 * Consulta cuja sigla é o próprio nome (Consulta Horista, OdontoPrev, Programa SiM Saude) só existe
 * em agendamento antigo: o original não a oferece para marcar.
 */
function oferecidaParaMarcar(c: AgendaClassificacao): boolean {
  if (!c.sigla) return false;
  return c.tipo === 'EXAME' || c.sigla !== c.valor;
}

/**
 * Opções do campo Classificação, da lista do back filtrada pelo tipo, gravando a sigla. "Encaixe"
 * (exame) só para quem pode encaixar. Retorno esgotado fica desabilitado, exceto para supervisão.
 */
export function opcoesClassifModal(
  tipo: AgendaTipoAgendamento,
  lista: AgendaClassificacao[],
  encaixa: boolean,
  retornos: Record<string, AgendaRetornoDisponivel | null>,
  supervisor: boolean,
): OpcaoClassif[] {
  return lista
    .filter(c => c.tipo === tipo && oferecidaParaMarcar(c))
    .filter(c => tipo !== 'EXAME' || c.sigla !== CLASSIF_EXAME_ENCAIXE || encaixa)
    .map(c => {
      const r = tipo === 'CONSULTA' && ehRetorno(c.sigla) ? retornos[c.sigla] : null;
      if (!r) return { valor: c.sigla, rotulo: c.rotulo, desabilitada: false };
      return { valor: c.sigla, rotulo: `${c.rotulo}${sufixoRetorno(r)}`, desabilitada: !r.permitido && !supervisor };
    });
}

function sufixoRetorno(r: AgendaRetornoDisponivel): string {
  if (!r.permitido) return r.temRegra === false ? ' (sem regra configurada)' : ` (esgotado${contagem(r)})`;
  const c = contagem(r);
  return c ? ` (${c.slice(3)})` : '';
}

function contagem(r: AgendaRetornoDisponivel): string {
  return r.qtLimite !== null && r.qtLimite < LIMITE_RETORNO_INFINITO ? ` · ${r.qtAtual}/${r.qtLimite}` : '';
}

/** A do horário só vale se existir e estiver habilitada; senão, a padrão ou a primeira habilitada. */
export function classifInicial(opcoes: OpcaoClassif[], doSlot: string | null | undefined, padrao: string): string {
  const valida = (v: string | null | undefined) => opcoes.find(o => o.valor === v && !o.desabilitada);
  return (valida(doSlot) ?? valida(padrao) ?? opcoes.find(o => !o.desabilitada))?.valor ?? '';
}

// --- Convênio 224 -------------------------------------------------------------------------------

export interface CategoriasPermitidas {
  lista: AgendaOpcao[];
  /** Motivo do select desabilitado; null = livre. */
  bloqueio: string | null;
  /** No 224 a única categoria possível já vem escolhida. */
  forcada: string | null;
}

function categoriaDoPlano(plano: AgendaPlanoPaciente | null): string | null {
  return plano?.temPlano && plano.cdConvenio === CONVENIO_CARTAO ? plano.cdCategoria : null;
}

/**
 * Cartão de Benefícios só com a categoria do plano ativo do paciente. É conveniência de tela:
 * quem recusa de verdade é a gravação, com o mesmo plano.
 */
export function categoriasPermitidas(
  cdConvenio: number | null,
  categorias: AgendaOpcao[],
  temPaciente: boolean,
  plano: AgendaPlanoPaciente | null,
): CategoriasPermitidas {
  if (!cdConvenio) return { lista: [], bloqueio: 'Selecione o convênio', forcada: null };
  if (cdConvenio !== CONVENIO_CARTAO) {
    return categorias.length
      ? { lista: categorias, bloqueio: null, forcada: null }
      : { lista: [], bloqueio: 'Nenhuma categoria liberada para esta unidade', forcada: null };
  }
  if (!temPaciente) return { lista: [], bloqueio: 'Escolha o paciente', forcada: null };
  const cat = categoriaDoPlano(plano);
  if (!cat) return { lista: [], bloqueio: 'Paciente sem plano do cartão', forcada: null };
  const lista = categorias.filter(c => String(c.codigo) === String(cat));
  if (!lista.length) return { lista: [], bloqueio: 'Plano do paciente não liberado nesta unidade', forcada: null };
  return { lista, bloqueio: null, forcada: String(cat) };
}

/** Pré-seleção: o plano do cartão se a unidade o oferece, senão Particular. */
export function convenioInicial(convenios: AgendaOpcao[], plano: AgendaPlanoPaciente | null): number | null {
  const tem = (cd: number | null | undefined) => cd != null && convenios.some(c => c.codigo === cd);
  if (categoriaDoPlano(plano) && tem(CONVENIO_CARTAO)) return CONVENIO_CARTAO;
  return tem(CONVENIO_PARTICULAR) ? CONVENIO_PARTICULAR : null;
}

export function categoriaInicial(cdConvenio: number | null, categorias: AgendaOpcao[]): string | null {
  if (cdConvenio === CONVENIO_PARTICULAR && categorias.some(c => String(c.codigo) === CATEGORIA_PARTICULAR)) {
    return CATEGORIA_PARTICULAR;
  }
  return categorias.length === 1 ? String(categorias[0].codigo) : null;
}

// --- Especialidade ------------------------------------------------------------------------------

export interface EspecialidadeAgenda {
  mista: boolean;
  opcoes: AgendaEspecialidadeDaAgenda[];
  /** Pré-seleção: a do filtro, se a agenda atende; nunca a titular por palpite. */
  escolhida: number | null;
}

export function especialidadeDaAgenda(lista: AgendaEspecialidadeDaAgenda[], doFiltro: number | null): EspecialidadeAgenda {
  if (lista.length <= 1) return { mista: false, opcoes: lista, escolhida: lista[0]?.cdEspecialidade ?? null };
  const valida = doFiltro != null && lista.some(e => e.cdEspecialidade === doFiltro);
  return { mista: true, opcoes: lista, escolhida: valida ? doFiltro : null };
}

/** Mista: só a escolhida (vazia = falta). Única: a da agenda, depois a do horário, depois a do filtro. */
export function especialidadeEfetiva(
  esp: EspecialidadeAgenda | null,
  doSlot: number | null | undefined,
  doFiltro: number | null | undefined,
): number | null {
  if (esp?.mista) return esp.escolhida;
  return esp?.escolhida || doSlot || doFiltro || null;
}

// --- Formulário ---------------------------------------------------------------------------------

export interface EstadoFormulario {
  modo: AgendaModoAgendamento;
  tipo: AgendaTipoAgendamento;
  cdPessoaFisica: string | null;
  cdConvenio: number | null;
  cdCategoria: string | null;
  categoriaBloqueada: boolean;
  cdCanal: number | null;
  especialidadeMista: boolean;
  cdEspecialidade: number | null;
  /** Só falta enquanto a lista de especialidades da agenda não respondeu. */
  especialidadePendente: boolean;
  nrSeqProcInterno: number | null;
  ieClassifAgenda: string;
  classifDesabilitada: boolean;
  /** O mesmo exame no mesmo dia: trava como no original. */
  duplicado: boolean;
  hrInicio: string;
  nrMinutoDuracao: number | null;
}

/** O que falta (ou impede) para gravar, na ordem do formulário. Vazio = pode salvar. */
export function camposFaltando(e: EstadoFormulario): string[] {
  const f: string[] = [];
  if (e.modo === 'encaixe') {
    if (!/^\d{2}:\d{2}$/.test(e.hrInicio || '')) f.push('horário');
    if (!e.nrMinutoDuracao || e.nrMinutoDuracao < 1) f.push('duração');
  }
  if (!e.cdPessoaFisica) f.push('paciente');
  if (e.tipo === 'EXAME' && !e.nrSeqProcInterno) f.push('exame');
  if (e.tipo === 'CONSULTA' && (e.especialidadePendente || (e.especialidadeMista && !e.cdEspecialidade))) f.push('especialidade');
  if (!e.cdConvenio) f.push('convênio');
  if (e.categoriaBloqueada || !e.cdCategoria) f.push('categoria');
  if (!e.ieClassifAgenda || e.classifDesabilitada) f.push('classificação');
  if (!e.cdCanal) f.push('canal de contato');
  if (e.duplicado) f.push('duplicidade');
  return f;
}

export interface DadosNovo {
  tipo: AgendaTipoAgendamento;
  nrSequencia: number;
  cdAgenda: number;
  cdPessoaFisica: string;
  cdConvenio: number;
  cdCategoria: string;
  ieClassifAgenda: string;
  cdCanal: number;
  dsObservacao: string;
  cdEspecialidade: number | null;
  nrSeqProcInterno: number | null;
  cdMedico: string | null;
  nrMinutoDuracao: number | null;
}

/** Corpo de POST agendamentos. Número vazio vai null: "" dá 400 no binding do back. */
export function corpoNovo(d: DadosNovo): AgendaNovoAgendamentoRequest {
  const exame = d.tipo === 'EXAME';
  const medicoNum = Number(d.cdMedico);
  return {
    tipo: d.tipo,
    nrSequencia: d.nrSequencia,
    cdAgenda: d.cdAgenda,
    cdPessoaFisica: d.cdPessoaFisica,
    cdConvenio: d.cdConvenio,
    cdCategoria: d.cdCategoria,
    ieClassifAgenda: d.ieClassifAgenda,
    cdCanal: d.cdCanal,
    dsObservacao: d.dsObservacao.trim() || null,
    cdEspecialidade: exame ? null : d.cdEspecialidade,
    nrSeqProcInterno: exame ? d.nrSeqProcInterno : null,
    cdMedicoReq: d.cdMedico && Number.isFinite(medicoNum) && medicoNum > 0 ? medicoNum : null,
    // Em agenda compartilhada é o único registro de quem atende; vazio cai no titular.
    cdMedicoExec: d.cdMedico || null,
    nrMinutoDuracao: d.nrMinutoDuracao || null,
  };
}

export interface DadosEncaixe {
  tipo: AgendaTipoAgendamento;
  cdAgenda: number;
  dtAgenda: string;
  hrInicio: string;
  nrMinutoDuracao: number;
  cdPessoaFisica: string;
  cdConvenio: number;
  cdCategoria: string;
  ieClassifAgenda: string;
  cdCanal: number;
  dsObservacao: string;
  cdEspecialidade: number | null;
  nrSeqProcInterno: number | null;
}

/** Corpo de POST agendamentos/encaixe. O encaixe não escolhe médico: vai no titular da agenda. */
export function corpoEncaixe(d: DadosEncaixe): AgendaEncaixeRequest {
  const exame = d.tipo === 'EXAME';
  return {
    tipo: d.tipo,
    cdAgenda: d.cdAgenda,
    dtAgenda: d.dtAgenda,
    hrInicio: d.hrInicio,
    nrMinutoDuracao: d.nrMinutoDuracao,
    cdPessoaFisica: d.cdPessoaFisica,
    cdConvenio: d.cdConvenio,
    cdCategoria: d.cdCategoria,
    ieClassifAgenda: d.ieClassifAgenda,
    cdCanal: d.cdCanal,
    dsObservacao: d.dsObservacao.trim() || null,
    cdEspecialidade: exame ? null : d.cdEspecialidade,
    nrSeqProcInterno: exame ? d.nrSeqProcInterno : null,
    cdMedicoExec: null,
  };
}

// --- Preço ----------------------------------------------------------------------------------------

export interface TextoPreco {
  valor: string | null;
  texto: string;
  sem: boolean;
}

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export function formatarBrl(v: number): string {
  return BRL.format(v);
}

/**
 * Sem preço no convênio escolhido continua "sem preço": nunca vira o valor particular. O nome do
 * plano só legenda o preço quando o convênio escolhido é o do cartão.
 */
export function textoPreco(p: AgendaPrecoPaciente | null, cdConvenio: number | null, plano: AgendaPlanoPaciente | null): TextoPreco {
  const v = p?.vlPlanoPaciente;
  if (v === null || v === undefined) {
    return { valor: null, texto: 'Sem preço cadastrado para este convênio e categoria. Não troca para particular.', sem: true };
  }
  const nomePlano = plano?.dsCategoria || p?.planName;
  const texto = cdConvenio === CONVENIO_CARTAO && nomePlano ? `Plano ${nomePlano}` : 'Valor do convênio escolhido';
  return { valor: BRL.format(v), texto, sem: false };
}

// --- Exibição de convênio + categoria ------------------------------------------------------------

export interface ExibicaoConvenio {
  linha1: string;
  sub: string | null;
  tooltip: string;
}

/**
 * No Cartão de Benefícios a categoria já é o nome do plano do paciente: repetir "Cartão de
 * Benefícios" na linha de cima só deixa a linha mais alta sem informar nada novo.
 */
export function exibicaoConvenio(cdConvenio: number | null | undefined, dsConvenio: string | null | undefined, dsCategoria: string | null | undefined): ExibicaoConvenio {
  const convenio = dsConvenio || '';
  const categoria = dsCategoria || '';
  const tooltip = [convenio, categoria].filter(Boolean).join(' · ');
  if (cdConvenio === CONVENIO_CARTAO && categoria) return { linha1: categoria, sub: null, tooltip };
  return { linha1: convenio, sub: categoria || null, tooltip };
}
